import { useEffect, useState } from 'react';
import { api } from '../api';
import type { UpcomingExpiration } from '../types';
import { RenewLeaseDialog } from '../components/RenewLeaseDialog';

type Count = { name: string; lease_count: number };
type Expiration = {
  id: number;
  name: string;
  expirationDate: string | null;
  commencementDate: string | null;
  paidStatus: string | null;
  rentalCurrency: string | null;
};

const upcomingLabel = (u: UpcomingExpiration): string =>
  [[u.propertyName, u.tenantName].filter(Boolean).join(' - '), u.unit]
    .filter(Boolean)
    .join(' — ') || `Lease #${u.id}`;

export const HomePage = () => {
  const [counts, setCounts] = useState<Count[]>([]);
  const [exps, setExps] = useState<Expiration[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingExpiration[]>([]);
  const [renewFor, setRenewFor] = useState<UpcomingExpiration | null>(null);
  const [loading, setLoading] = useState(true);

  const load = () =>
    Promise.all([api.leaseCounts(), api.currentMonthExpirations(), api.upcomingExpirations()])
      .then(([c, e, u]) => {
        setCounts(c);
        setExps(e as Expiration[]);
        setUpcoming(u);
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
  }, []);

  if (loading) return <div className="page">Loading…</div>;
  return (
    <div className="page">
      <h1>Dashboard</h1>
      <div className="dashboard-grid">
        <div>
          <h2>Lease counts by property</h2>
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>Property</th>
                  <th style={{ textAlign: 'right' }}>Leases</th>
                </tr>
              </thead>
              <tbody>
                {counts.map((c) => (
                  <tr key={c.name}>
                    <td>{c.name}</td>
                    <td style={{ textAlign: 'right' }}>{c.lease_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h2>Current month expirations</h2>
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>Lease</th>
                  <th>Expiration</th>
                  <th>Paid</th>
                </tr>
              </thead>
              <tbody>
                {exps.length === 0 && (
                  <tr>
                    <td colSpan={3} className="muted">
                      None
                    </td>
                  </tr>
                )}
                {exps.map((e) => (
                  <tr key={e.id}>
                    <td>{e.name}</td>
                    <td>{e.expirationDate ?? ''}</td>
                    <td>{e.paidStatus ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <h2>Expiring soon (90 days)</h2>
          <div className="table-wrap">
            <table className="grid">
              <thead>
                <tr>
                  <th>Lease</th>
                  <th>Expiration</th>
                  <th style={{ textAlign: 'right' }}>Days left</th>
                  <th>Last renewal</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {upcoming.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      None
                    </td>
                  </tr>
                )}
                {upcoming.map((u) => (
                  <tr key={u.id}>
                    <td>{upcomingLabel(u)}</td>
                    <td>{u.expirationDate}</td>
                    <td style={{ textAlign: 'right' }}>{u.daysLeft}</td>
                    <td>{u.lastRenewalDate ?? ''}</td>
                    <td>
                      <button onClick={() => setRenewFor(u)}>Renew</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {renewFor && (
        <RenewLeaseDialog
          leaseId={renewFor.id}
          leaseLabel={upcomingLabel(renewFor)}
          currentExpiration={renewFor.expirationDate}
          onClose={() => setRenewFor(null)}
          onRenewed={async () => {
            await load();
          }}
        />
      )}
    </div>
  );
};
